import { getE2eHeaders } from "./e2eAuth";

const STORAGE_KEY = "onenext.impersonate.v1";
const HEADER_NAME = "X-Impersonate-User-Id";

export type ImpersonationState = {
  userId: string;
  email: string;
};

const subscribers = new Set<() => void>();

// Cached snapshot so getImpersonation() returns a stable reference between
// notifications. Required by useSyncExternalStore — otherwise React sees a
// new object on every render and triggers "Maximum update depth exceeded".
let cachedSnapshot: ImpersonationState | null = null;
let cacheInitialised = false;

function readFromStorage(): ImpersonationState | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<ImpersonationState>;
    if (!parsed?.userId || !parsed?.email) return null;
    return { userId: parsed.userId, email: parsed.email };
  } catch {
    return null;
  }
}

function refreshSnapshot(): void {
  const next = readFromStorage();
  const prev = cachedSnapshot;
  if (
    !cacheInitialised ||
    (next === null) !== (prev === null) ||
    (next && prev && (next.userId !== prev.userId || next.email !== prev.email))
  ) {
    cachedSnapshot = next;
    cacheInitialised = true;
  } else if (!cacheInitialised) {
    cachedSnapshot = next;
    cacheInitialised = true;
  }
}

export function getImpersonation(): ImpersonationState | null {
  if (!cacheInitialised) refreshSnapshot();
  return cachedSnapshot;
}

export function getImpersonationHeaders(): Record<string, string> {
  const s = getImpersonation();
  // Always merge E2E headers too: many manual fetch clients
  // (`chat-api.ts`, `chat-stream.ts`, `assistant-stream.ts`,
  // `labels-api.ts`, `admin-api.ts`, `settings.tsx`) call this helper
  // directly instead of going through the generated API client. In
  // E2E_TEST_MODE, we need them to forward `x-e2e-user-id`. Outside
  // test mode, `getE2eHeaders()` returns `{}`, so this is a no-op.
  const e2e = getE2eHeaders();
  return s ? { [HEADER_NAME]: s.userId, ...e2e } : { ...e2e };
}

export function startImpersonation(state: ImpersonationState): void {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  refreshSnapshot();
  subscribers.forEach((cb) => cb());
}

export function stopImpersonation(): void {
  if (typeof window === "undefined") return;
  window.localStorage.removeItem(STORAGE_KEY);
  refreshSnapshot();
  subscribers.forEach((cb) => cb());
}

export function subscribeImpersonation(cb: () => void): () => void {
  subscribers.add(cb);
  // Cross-tab sync: react to localStorage changes from other tabs.
  const onStorage = (e: StorageEvent) => {
    if (e.key === STORAGE_KEY) {
      refreshSnapshot();
      cb();
    }
  };
  if (typeof window !== "undefined") {
    window.addEventListener("storage", onStorage);
  }
  return () => {
    subscribers.delete(cb);
    if (typeof window !== "undefined") {
      window.removeEventListener("storage", onStorage);
    }
  };
}

export const IMPERSONATION_HEADER_NAME = HEADER_NAME;
